import type { LoadedApp } from '@/features/page-editor/store/types'

const MAX_SECTION_CHARS = 60_000

export interface CogoAppChatContext {
  kind: 'cogo-app-context.v1'
  truncated: boolean
  includedSections: string[]
  manifest?: unknown
  blocks?: Record<string, unknown>
  data?: Record<string, unknown>
  flows?: Record<string, unknown>
  tokens?: unknown
  designSystem?: Record<string, string>
}

export function buildCogoAppChatContext(app: LoadedApp | null | undefined): CogoAppChatContext | undefined {
  if (!app) return undefined

  const context: CogoAppChatContext = {
    kind: 'cogo-app-context.v1',
    truncated: false,
    includedSections: [],
  }

  addSection(context, 'manifest', app.manifest)
  addSection(context, 'blocks', buildBlocksContext(app.blocks))
  addSection(context, 'data', app.data)
  addSection(context, 'flows', app.flows)
  addSection(context, 'tokens', app.tokens)
  addSection(context, 'designSystem', app.designSystem)

  return context
}

function buildBlocksContext(blocks: LoadedApp['blocks']): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [name, block] of Object.entries(blocks ?? {})) {
    const record = block as unknown as Record<string, unknown>
    out[name] = compactObject({
      meta: record.meta,
      ui: record.ui,
      events: record.events,
      flow: record.flow,
      actionflow: record.actionflow,
      data: record.data,
      datasource: record.datasource,
      variables: record.variables,
      state: record.state,
    })
  }
  return out
}

function addSection(
  context: CogoAppChatContext,
  key: Exclude<keyof CogoAppChatContext, 'kind' | 'truncated' | 'includedSections'>,
  value: unknown,
): void {
  if (value == null) return
  ;(context as unknown as Record<string, unknown>)[key] = boundedJsonValue(value, key)
  context.includedSections.push(key)
  if (isTruncated((context as unknown as Record<string, unknown>)[key])) {
    context.truncated = true
  }
}

function boundedJsonValue(value: unknown, label: string): unknown {
  const cloned = cloneJson(value)
  const serialized = JSON.stringify(cloned)
  if (!serialized || serialized.length <= MAX_SECTION_CHARS) return cloned
  return {
    _truncated: true,
    _section: label,
    _jsonChars: serialized.length,
    _maxChars: MAX_SECTION_CHARS,
    preview: serialized.slice(0, MAX_SECTION_CHARS),
  }
}

function compactObject(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v != null))
}

function cloneJson(value: unknown): unknown {
  return JSON.parse(JSON.stringify(value))
}

function isTruncated(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && (value as Record<string, unknown>)._truncated)
}
